import { Grid } from '@mui/material';
import {
  HSpacer,
  RoutalPalette,
  RoutalSelectAutocomplete,
  RoutalSelectAutocompleteMultiSelectOption,
} from '@nx-smartmonkey/ui';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

import LocationOnIcon from '../../../../../../assets/svg/LocationOnIcon';
import { intl } from '../../../../../../helpers/IntGlobalProvider';
import Text from '../../../../../../react-components/Text';
import { FieldDefinition } from '../fields/FieldDefinitions';
import { FieldMapping } from '../fields/FieldMapping';
import { MapFieldsColumnRow, ROW_HEIGHT } from './MapFieldsColumnRow';
import { MapFieldsColumnSummary } from './MapFieldsColumnSummary';

const EllipsisText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowRight = styled.div`
  width: 0;
  height: 0;
  border-top: 19px solid transparent;
  border-bottom: 19px solid transparent;
  border-left: 8px solid ${RoutalPalette.grey1};
  position: absolute;
  right: -8px;
  top: 0px;
`;

interface MapFieldsColumnProps {
  columnName: string;
  columnIndex: number;
  isLastColumn: boolean;
  mapping: FieldMapping;
  fields: FieldDefinition[];
  addressFields?: FieldDefinition[];
  setMapping: (mapping: FieldMapping) => void;
}

export const MapFieldsColumn = ({
  columnName,
  columnIndex,
  isLastColumn,
  mapping,
  fields,
  addressFields,
  setMapping,
}: MapFieldsColumnProps) => {
  const rows = mapping.getDataByColumnIndex(columnIndex);

  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      style={{
        borderRadius: `5px`,
        marginBottom: !isLastColumn ? 20 : 0,
      }}
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          width: `60%`,
          minWidth: `60%`,
          border: `1px solid ${RoutalPalette.grey4}`,
          borderRadius: `5px`,
          minHeight: `calc(min(${ROW_HEIGHT * 3}px, ${rows.length * ROW_HEIGHT}px) + 37px)`,
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ position: `relative`, borderBottom: `1px solid ${RoutalPalette.grey4}`, height: `44px` }}
        >
          <div
            style={{
              position: `absolute`,
              width: `48%`,
              height: `38px`,
              top: `-1px`,
              left: `-1px`,
              borderTopLeftRadius: `5px`,
              backgroundColor: `${RoutalPalette.grey1}`,
            }}
          >
            <Grid container style={{ height: `100%`, width: `100%`, paddingLeft: `12px` }} alignContent="center">
              <EllipsisText style={{ color: `white` }}>{columnName}</EllipsisText>
            </Grid>
            <ArrowRight />
          </div>

          <RoutalSelectAutocomplete
            id="mapping-field-selection"
            containerStyle={{
              width: `100%`,
              border: `unset`,
              height: `36px`,
              margin: 0,
              display: `flex`,
              flexDirection: `row`,
              justifyContent: `flex-end`,
              alignItems: `center`,
              borderTopLeftRadius: `5px`,
              borderTopRightRadius: `5px`,
            }}
            style={{
              border: `unset`,
              backgroundColor: `transparent`,
              width: `50%`,
              height: `100%`,
            }}
            searchPlaceholder={intl.formatMessage({ id: `plans.dialogs.upload_stops.map_fields.search_columns` })}
            searchEmptyText={intl.formatMessage({ id: `no_results` })}
            hideSeparator
            searchWidth={280}
            popoverProps={{
              options: [
                {
                  key: `not-selected`,
                  width: 280,
                  listItemStyles: {
                    whiteSpace: `normal` as any,
                    overflow: `auto`,
                    fontSize: `12px`,
                    backgroundColor: RoutalPalette.grey3,
                    cursor: !mapping.getByColumnIndex(columnIndex) ? `default` : `pointer`,
                  },
                  onClick: async () => {
                    if (mapping.getByColumnIndex(columnIndex)) {
                      setMapping(mapping.removeByColumn(columnIndex));
                    }
                  },
                  notSearchable: true,
                  content: (
                    <Text
                      intlId="not_selected"
                      variant={!mapping.getByColumnIndex(columnIndex) ? `label` : `body`}
                      style={{
                        textAlign: `justify`,
                        cursor: !mapping.getByColumnIndex(columnIndex) ? `default` : `pointer`,
                        width: `100%`,
                      }}
                    />
                  ),
                },
                ...FieldMapping.sortFields(fields, addressFields).map(
                  (field, index): RoutalSelectAutocompleteMultiSelectOption => {
                    return {
                      key: `option-${index}`,
                      width: 280,
                      listItemStyles: {
                        whiteSpace: `normal` as any,
                        overflow: `auto`,
                        fontSize: `12px`,
                      },
                      searchText: field.label ?? intl.formatMessage({ id: `properties.service.${field.id}` }),
                      onClick: () => {
                        setMapping(
                          mapping.add(field, {
                            index: columnIndex,
                            name: columnName,
                          })
                        );
                      },
                      content: (
                        <Grid container direction="row" wrap="nowrap">
                          <Text
                            style={{
                              textAlign: `justify`,
                              cursor: `pointer`,
                              width: `100%`,
                            }}
                            variant={mapping.getById(field.id) ? `label` : `body`}
                          >
                            {`${field.label ?? intl.formatMessage({ id: `properties.service.${field.id}` })}${
                              field.required ? `*` : ``
                            }`}
                          </Text>

                          {FieldMapping.isAddressField(field) ? (
                            <>
                              <HSpacer small />
                              <LocationOnIcon height="16px" width="16px" fill={RoutalPalette.primary00} />
                            </>
                          ) : null}
                        </Grid>
                      ),
                    };
                  }
                ),
              ],
            }}
          >
            {mapping.getByColumnIndex(columnIndex) ? (
              <Grid container direction="row" wrap="nowrap">
                {FieldMapping.isAddressField(mapping.getByColumnIndex(columnIndex)!.field) ? (
                  <>
                    <LocationOnIcon height="16px" width="16px" fill={RoutalPalette.primary00} />
                    <HSpacer small />
                  </>
                ) : null}

                <Text style={{ cursor: `pointer`, width: `100%`, textAlign: `justify` }}>
                  {`${
                    mapping.getByColumnIndex(columnIndex)!.field.label ??
                    intl.formatMessage({ id: `properties.service.${mapping.getByColumnIndex(columnIndex)!.field.id}` })
                  }${mapping.getByColumnIndex(columnIndex)!.field?.required ? `*` : ``}`}
                </Text>
              </Grid>
            ) : (
              <Text
                style={{ cursor: `pointer`, width: `100%`, textAlign: `justify`, color: RoutalPalette.neutral20 }}
                intlId="not_selected"
              />
            )}
          </RoutalSelectAutocomplete>
        </Grid>

        <div style={{ height: `max(min(${ROW_HEIGHT * rows.length}px, 100%), ${ROW_HEIGHT * 3}px)` }}>
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <List
                height={height!}
                width={width!}
                itemCount={rows.length}
                itemSize={ROW_HEIGHT}
                itemData={{
                  mapping,
                  columnIndex,
                }}
              >
                {MapFieldsColumnRow}
              </List>
            )}
          </AutoSizer>
        </div>
      </Grid>

      <HSpacer />

      <MapFieldsColumnSummary columnIndex={columnIndex} mapping={mapping} setMapping={setMapping} />
    </Grid>
  );
};
